/*
 * 单条消息。
 *
 * 气泡几何沿用旧产品：大圆角药丸 + 说话方那一侧的上角削平，没有小尖角、
 * 没有阴影（DialogItem.tsx borderRadius: 30 / borderTopLeftRadius: 0）。
 * 这里把圆角从 30 收到 26、缺角从 0 放到 4，边缘不至于割手，形状仍认得出。
 *
 * 相对旧版补了三件旧版没有的事：
 *   - 发送状态。旧版乐观发送后没有失败态，网络断了消息就静静消失。
 *   - 兜底标记。contracts 的 isFallback 说「非模型生成，客户端**可**据此
 *     弱化呈现」——是可以，不是必须。这里保留 msg--fallback 这个类名供排查与
 *     测试选中，但**不给它任何视觉差异**：斜体加粉色左边界会把她的话画成引用块。
 *     合规上也不需要，要显著标识的是模型生成内容，兜底句恰恰是写死的常量。
 *   - 进场动效。逐条上浮，配合旧版「每字 10ms」的排队出现节奏。
 *
 * ## 操作条：点气泡才出现，停在右下角
 *
 * 复制 / 朗读 / 赞 / 踩 / 删除挤在一枚粉色药丸里，压在气泡右下沿。
 * 这是旧版 DialogList + DialogItem 的排法（`extra` 那个绝对定位的 pill）。
 *
 * 喇叭**曾经常驻在气泡下方**，现在收进这条里。常驻的问题不是占地方，
 * 是它孤零零挂在那儿时看起来像消息的一部分，而其余四个动作没有落脚点——
 * 五个动作放五个常驻按钮，整个消息流会被按钮填满。
 *
 * 同一时刻只有一条气泡展开操作条，由上层持有（`openId`）。各管各的话，
 * 点第二条时第一条的药丸还开着，屏幕上会同时浮着好几排按钮。
 */

import type { ConversationMessage } from "@dreamverse/contracts";
import { Link } from "react-router-dom";
import { MessagePartsRenderer } from "../../components/MessagePartRenderer";
import { AiGeneratedMark } from "../../components/AiDisclosure";
import { SpeakButton, type SpeakState } from "../../components/SpeakButton";
import {
  IconCopy,
  IconRetry,
  IconThumbDown,
  IconThumbUp,
  IconTrash,
  IconUser,
} from "../../components/Icons";

function isNestedInteractiveTarget(target: EventTarget | null, bubble: HTMLElement): boolean {
  if (!(target instanceof Element)) return false;
  const interactive = target.closest(
    "button, a, input, textarea, select, summary, [role='button'], [role='link']",
  );
  return interactive !== null && interactive !== bubble;
}
import { useSession } from "../../state/session";

/** 消息在客户端的投递状态。服务端已确认的消息没有这个字段。 */
export type DeliveryState = "sending" | "sent" | "failed";

export interface ChatEntry {
  message: ConversationMessage;
  delivery?: DeliveryState;
  /** 发送失败的原因摘要。只在 delivery === 'failed' 时有值。 */
  failureNote?: string;
}

export function formatTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleTimeString("zh-CN", {
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** 这条消息的纯文本，用于复制。非文本片段直接跳过，不编占位符。 */
function plainTextOf(message: ConversationMessage): string {
  return message.parts
    .filter((p): p is Extract<typeof p, { type: "text" }> => p.type === "text")
    .map((p) => p.text)
    .join("\n");
}

export function MessageBubble({
  entry,
  speakerName,
  avatarUrl,
  streaming,
  onRetry,
  onNameClick,
  userAvatarLinksToProfile = true,
  open,
  onToggleOpen,
  speakState,
  onToggleSpeak,
  rating,
  onRate,
  onDelete,
  onCopy,
  onRetrySkill,
  retryingSkillExecutionId,
  imagePreviewUrls,
  onImagePreviewConsumed,
  showAiGeneratedMark,
}: {
  entry: ChatEntry;
  /** 角色展示名。由上层从服务端数据或本地兜底决定，本组件不判断是谁。 */
  speakerName: string;
  avatarUrl: string | null;
  /**
   * 这条是仍在增长的流式回复。三点必须跟在当前文字末尾，直到完成事件到达；
   * 否则模型先说「我帮你搜一下」再等待工具结果时，看起来会像已经回答完了。
   */
  streaming?: boolean;
  onRetry?: (clientMessageId: string) => void;
  /**
   * 点角色名的回调。旧版就是点名字弹形态说明与切换入口
   * （docs/PERSONA_FORMS.md §6），这里沿用，不再另放一枚名牌。
   */
  onNameClick?: () => void;
  /** 用户侧头像是否可进入“我的”；独立知识问答必须关闭这条产品导航。 */
  userAvatarLinksToProfile?: boolean;
  /**
   * 操作条开着没有。**由上层统一持有**——同一时刻只该有一条气泡是展开的，
   * 各管各的会让屏幕上同时浮着好几排按钮。
   * 不传表示这条消息没有操作条（比如还在流式生成、还没落库）。
   */
  open?: boolean;
  onToggleOpen?: () => void;
  /**
   * 朗读状态。**由上层统一持有**，不是每个气泡自己管——
   * 同一时刻只允许一条消息在响，各管各的会让两个声音叠在一起。
   * 不传表示当前环境不支持朗读，按钮整个不出现。
   */
  speakState?: SpeakState;
  onToggleSpeak?: () => void;
  /** 当前评价。`null` / 不传都表示没评过。 */
  rating?: 1 | -1 | null;
  /** 点赞点踩。再点同一个就是收回，由上层换算成 null。 */
  onRate?: (rating: 1 | -1) => void;
  onDelete?: () => void;
  onCopy?: (text: string) => void;
  /** 失败的持久化 Skill 重新执行；点击本身即为本次扣费授权。 */
  onRetrySkill?: (executionId: string) => void;
  retryingSkillExecutionId?: string | null;
  /** 本轮刚上传图片的本地预览；服务器缩略图就绪前保持原图可见。 */
  imagePreviewUrls?: ReadonlyMap<string, string>;
  onImagePreviewConsumed?: (assetId: string, src: string) => void;
  /** 运营开关开启时，在真正的模型回复底部显示唯一一处 AI 生成标识。 */
  showAiGeneratedMark?: boolean;
}) {
  const { message, delivery, failureNote } = entry;
  const mine = message.role === "user";
  const fallback = message.isFallback === true;
  const channelOrigin = message.origin?.kind === "channel" ? message.origin : null;

  /*
   * 用户自己的头像。**只有 `mine` 那一支用得上**，但 Hook 不能写在条件里。
   * 与 `avatarUrl`（角色的、上层传进来的）是两回事，别混用。
   */
  const { accountAvatarUrl } = useSession();

  /**
   * 这条消息能不能被朗读：只有她说的、已经落库的文本消息。
   *
   *   - 用户自己的消息没有朗读的意义。
   *   - 还没送达的消息（delivery 有值）服务端还没有对应的 Message 行，
   *     而合成要用 messageId 做幂等键，没有它就会重复扣费。
   *
   * **兜底句可以朗读**（2026-08-01 起）。以前不行，理由是「写死的常量，
   * 为它花灵力说不过去」——那是在替用户做决定，花的是他自己的 2 点灵力，
   * 而他明确点了那个按钮。
   *
   * ⚠ 网络失败时本地插的那句离线兜底话（`ChatPage.appendOfflineLine`）
   * **没有 `delivery`**，靠这里挡不住；它安全是因为上层压根不传
   * `speakState` / `onRate` / `onDelete`。别把那三个handler顺手补上去，
   * 它的 id 是 `offline-<时间戳>`，服务端不认得。
   *
   * `speakState` / `onToggleSpeak` 是否传入由上层决定：当前环境不支持播放时
   * 整个按钮不出现，而不是出现一个点了没反应的按钮。
   */
  const speakable =
    !mine &&
    !channelOrigin &&
    delivery === undefined &&
    message.parts.some((p) => p.type === "text");

  /**
   * 评价只对她说的、已落库的消息开放。给自己说的话点赞没有意义。
   *
   * **兜底句可以评价，也就可以收藏**（收藏就是 `rating === 1`）。
   * 她连不上灵境时说的那句话，用户想标记下来是他的自由。
   */
  const rateable = !mine && !channelOrigin && delivery === undefined;

  /** 复制只要求有文字。用户自己那条也能复制。 */
  const text = plainTextOf(message);
  const copyable = text !== "" && onCopy !== undefined;
  /** 删除要求服务端认得这个 id，所以乐观条目与流式占位都不给。 */
  const deletable = !channelOrigin && delivery === undefined && onDelete !== undefined;

  const hasActions =
    onToggleOpen !== undefined &&
    (copyable ||
      deletable ||
      (rateable && onRate !== undefined) ||
      (speakable && speakState !== undefined));

  const classes = [
    "msg",
    mine ? "msg--out" : "msg--in",
    fallback ? "msg--fallback" : "",
    channelOrigin ? "msg--channel" : "",
    delivery === "failed" ? "msg--failed" : "",
    delivery === "sending" ? "msg--sending" : "",
    streaming ? "msg--streaming" : "",
    open && hasActions ? "msg--open" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <article className={classes}>
      <div className="msg__head">
        {!mine ? (
          <span
            className={`msg__avatar${avatarUrl ? "" : " msg__avatar--wisp"}`}
            aria-hidden
          >
            {avatarUrl ? <img src={avatarUrl} alt="" /> : null}
          </span>
        ) : null}

        {mine ? (
          <span className="msg__name">我</span>
        ) : onNameClick ? (
          <button
            type="button"
            className="msg__name msg__name--action"
            onClick={onNameClick}
            title="她现在是什么状态"
          >
            {speakerName}
          </button>
        ) : (
          <span className="msg__name">{speakerName}</span>
        )}

        {/*
          站外来源现在由整组“相遇”折叠条统一显示，单条消息右上角不再重复画 QQ 来源气泡。
          先保留原渲染代码，之后若需要支持脱离相遇块单独展示一条站外消息，可以直接恢复。

          {channelOrigin ? (
            <span
              className="msg__origin"
              title={`这句话发生在站外${channelOrigin.endpointLabel}`}
            >
              {channelOrigin.endpointLabel} · {formatTime(message.createdAt)}
            </span>
          ) : null}
        */}

        {mine && userAvatarLinksToProfile ? (
          /*
            用户自己那一侧的头像。

            两件事和她那一侧不同：

            ① **画的是真头像，不是一个通用小人图标。** 数据从 `useSession()` 拿而
               不是像 `avatarUrl`（角色的）那样从上层传——这个组件有四个渲染点
               （聊天页三处 + 回忆点滴的对话回放），传参就有漏传的可能，
               而漏传的表现是「别的地方都有脸，这里是个占位图标」，
               不报错、不显眼，正是过去两轮反复在修的那类 bug。

            ② **它是个链接，点进「我的」。** 因此 `aria-hidden` 要去掉——
               藏起一个可聚焦元素会让键盘用户 Tab 到一个读屏读不出来的东西。
          */
          <Link
            to="/profile"
            className={`msg__avatar msg__avatar--user${
              accountAvatarUrl ? " msg__avatar--photo" : ""
            }`}
            aria-label="我的"
            title="我的"
          >
            {accountAvatarUrl ? (
              <img src={accountAvatarUrl} alt="" />
            ) : (
              <IconUser size={14} />
            )}
          </Link>
        ) : mine ? (
          <span
            className={`msg__avatar msg__avatar--user${
              accountAvatarUrl ? " msg__avatar--photo" : ""
            }`}
            aria-hidden="true"
          >
            {accountAvatarUrl ? (
              <img src={accountAvatarUrl} alt="" />
            ) : (
              <IconUser size={14} />
            )}
          </span>
        ) : null}
      </div>

      {/*
        气泡本体 + 压在右下沿的操作条。
        包一层是因为操作条是相对气泡绝对定位的，而 .msg 那层还装着头像行和
        失败说明——挂在 .msg 上会以整条消息的下沿为基准，飘到失败说明底下去。
      */}
      <div className="msg__body">
        <div
          className="msg__bubble"
          onClick={
            hasActions
              ? (event) => {
                  if (isNestedInteractiveTarget(event.target, event.currentTarget)) return;
                  onToggleOpen?.();
                }
              : undefined
          }
          role={hasActions ? "button" : undefined}
          tabIndex={hasActions ? 0 : -1}
          aria-expanded={hasActions ? open === true : undefined}
          onKeyDown={
            hasActions
              ? (event) => {
                  if (isNestedInteractiveTarget(event.target, event.currentTarget)) return;
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    onToggleOpen?.();
                  }
                }
              : undefined
          }
        >
          <MessagePartsRenderer
            parts={message.parts}
            {...(onRetrySkill && !channelOrigin ? { onRetrySkill } : {})}
            {...(retryingSkillExecutionId !== undefined
              ? { retryingSkillExecutionId }
              : {})}
            {...(imagePreviewUrls ? { imagePreviewUrls } : {})}
            {...(onImagePreviewConsumed ? { onImagePreviewConsumed } : {})}
          />
          {streaming ? <TypingDots inline label="仍在回复" /> : null}
          {showAiGeneratedMark && !mine && !fallback && !channelOrigin ? (
            <AiGeneratedMark />
          ) : null}
        </div>

        {hasActions && open ? (
          /*
            ⚠ 每一颗都要 stopPropagation：气泡本身是可点的（开合这条），
            不拦的话点任何一个动作都会顺带把操作条关掉，
            而「点赞」这种可以连点两次（赞→收回）的动作会因此只生效第一次。
          */
          <div
            className="msg__actions fade-in"
            onClick={(event) => event.stopPropagation()}
            role="toolbar"
            aria-label="这条消息的操作"
          >
            {copyable ? (
              <button
                type="button"
                className="msg__action"
                onClick={() => onCopy(text)}
                aria-label="复制这句话"
                title="复制"
              >
                <IconCopy size={15} />
              </button>
            ) : null}

            {speakable && speakState !== undefined && onToggleSpeak !== undefined ? (
              <SpeakButton
                state={speakState}
                onToggle={onToggleSpeak}
              />
            ) : null}

            {rateable && onRate !== undefined ? (
              <>
                <button
                  type="button"
                  className={`msg__action${rating === 1 ? " is-on" : ""}`}
                  onClick={() => onRate(1)}
                  aria-pressed={rating === 1}
                  aria-label={rating === 1 ? "收回好评" : "这句说得好"}
                  title={rating === 1 ? "收回好评" : "这句说得好"}
                >
                  <IconThumbUp size={15} />
                </button>
                <button
                  type="button"
                  className={`msg__action${rating === -1 ? " is-on" : ""}`}
                  onClick={() => onRate(-1)}
                  aria-pressed={rating === -1}
                  aria-label={rating === -1 ? "收回差评" : "这句不太对"}
                  title={rating === -1 ? "收回差评" : "这句不太对"}
                >
                  <IconThumbDown size={15} />
                </button>
              </>
            ) : null}

            {deletable ? (
              <button
                type="button"
                className="msg__action msg__action--danger"
                onClick={onDelete}
                aria-label="删掉这句话，她也会忘记"
                title="删掉这句话，她也会忘记"
              >
                <IconTrash size={15} />
              </button>
            ) : null}
          </div>
        ) : null}
      </div>

      {/*
        ⚠ 这里曾经有一条「正在送出」+ 呼吸圆点。**已删，别加回来。**

        它挂在用户自己那条消息下面，而那一刻屏幕上还有她的 TypingIndicator
        在跳——两处都在说「等一下」，而用户等的从来不是自己那句话发没发出去。
        送出中的信号留 `.msg--sending .msg__bubble { opacity: .66 }` 那一条
        就够了，是同一条消息自己变淡，不额外占一行。

        **失败态保留**：那个必须说，而且要留住重试的入口。
      */}
      <footer className="msg__meta">
        {delivery === "failed" ? (
          <>
            <span className="msg__status msg__status--failed">未送达</span>
            {onRetry ? (
              <button
                type="button"
                className="btn btn--quiet"
                onClick={() => onRetry(message.id)}
              >
                <IconRetry size={13} />
                重试
              </button>
            ) : null}
          </>
        ) : null}

        {open || delivery === "failed" ? (
          <span className="msg__time">{formatTime(message.createdAt)}</span>
        ) : null}
      </footer>

      {failureNote ? <p className="msg__failure-note">{failureNote}</p> : null}
    </article>
  );
}

/** 三点动画本体。独立等待气泡与流式正文末尾共用，避免两套状态长得不一致。 */
export function TypingDots({
  inline = false,
  label = "正在输入",
}: {
  inline?: boolean;
  label?: string;
}) {
  return (
    <span className={`typing${inline ? " typing--inline" : ""}`} aria-label={label}>
      <i />
      <i />
      <i />
    </span>
  );
}

/** 首字前的等待态：三点保留节奏，真实工具活动出现时在旁边补一行角色文案。 */
export function TypingStatus({
  activityText,
}: {
  activityText?: string | null | undefined;
}) {
  return (
    <span className="typing-status">
      <TypingDots label={activityText ?? "正在输入"} />
      {activityText ? <span className="typing-status__text">{activityText}</span> : null}
    </span>
  );
}

/**
 * 输入指示。
 *
 * 旧版是「正在输入……」加一个 antd-mobile 的 Loading 圆环。这里换成三点跳动，
 * 并且**只在真的建立了流式连接后才出现**——离线时不会闪一下再消失，
 * 那种假动作会让人以为消息发出去了。
 */
export function TypingIndicator({
  speakerName,
  avatarUrl,
  activityText,
}: {
  speakerName: string;
  avatarUrl: string | null;
  activityText?: string | null;
}) {
  return (
    <article className="msg msg--in msg--typing">
      <div className="msg__head">
        <span
          className={`msg__avatar${avatarUrl ? "" : " msg__avatar--wisp"}`}
          aria-hidden
        >
          {avatarUrl ? <img src={avatarUrl} alt="" /> : null}
        </span>
        <span className="msg__name">{speakerName}</span>
      </div>
      <div className="msg__bubble">
        <TypingStatus activityText={activityText} />
      </div>
    </article>
  );
}
